import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { z } from 'zod/v4'
import { sentryCaptureException, useLogError } from '@/src/common/hooks/useLogError'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import {
  useCardBenefitsParameterStore,
  CardBenefitSchema,
  type CardBenefit,
} from '../statsig/store/useCardBenefitsParameterStore'
import { REFERRAL_BONUS_OFFER_PARTNER_NAME, INSTACART_PARTNER_NAME } from '../constants'
import { useReferralBonusOfferEligibility } from './useReferralBonusOfferEligibility'
import { useBenefits } from './useBenefits'
import { useBenefitsEnrollments } from './useBenefitsEnrollments'

const order = [
  'Referral Bonus Offer',
  "Lowe's Home Improvement",
  'Big Box Membership Credit',
  'Instacart',
  'TurboTax',
  'Thumbtack',
  "The Farmer's Dog",
  'Wag!',
  'Armadillo',
  'Cozy Earth',
  'Home Improvement Credit',
] as const

const Order = z.enum(order)

type FilterType = 'All' | 'New' | 'Enrolled' | 'Not Enrolled'

/**
 * Represents a benefit with its API data and CMS content combined
 */
export type AvailableBenefit = {
  benefit: {
    id: string
    partnerName: string
    displayName: string
    isEnabled: boolean
    startDate: string | Date
    endDate?: string | Date | null
    refreshes?: string | null
    creditPerRefresh?: number | null
    creditRedemption?: string | null
  }
  cms: CardBenefit
}

export function useAvailableBenefits(selectedFilter: FilterType) {
  const logError = useLogError()
  const cardBenefitsStore = useCardBenefitsParameterStore()
  const cardBenefitsV1 = cardBenefitsStore.get('benefits_v1')

  const { featureGate: isInstacartEnabledGate, loadingStatus: isInstacartLoadingStatus } =
    useFeatureGate(FeatureGates.InstacartBenefitEnabled)

  const { data: benefits, isLoading: isBenefitsLoading } = useBenefits()
  const { data: userBenefitsEnrollments, isLoading: isUserBenefitsEnrollmentsLoading } =
    useBenefitsEnrollments()
  const { data: referralBonusOfferEligibility, isLoading: isReferralBonusOfferEligibilityLoading } =
    useReferralBonusOfferEligibility()

  const availableBenefits = useMemo(() => {
    if (
      !cardBenefitsV1 ||
      !benefits ||
      !userBenefitsEnrollments ||
      isReferralBonusOfferEligibilityLoading
    ) {
      return []
    }

    const isInstacartEnabled =
      isInstacartLoadingStatus === 'Ready' ? Boolean(isInstacartEnabledGate.value) : false

    return benefits
      .filter((benefit) => {
        if (!benefit.endDate) return false
        const today = new Date()

        const benefitStartDate = new Date(benefit.startDate)
        const benefitEndDate = new Date(benefit.endDate)
        const isBenefitActiveBetweenStartAndEndDate =
          benefitStartDate.getTime() <= today.getTime() &&
          today.getTime() <= benefitEndDate.getTime()

        // Enable Home Improvement only if they've enrolled it already
        const isEnrolledInHomeImprovementCredit =
          benefit.partnerName === 'Home Improvement Credit' &&
          userBenefitsEnrollments.find((b) => b.benefitId === benefit.id)?.isEnrolled

        return (
          (isBenefitActiveBetweenStartAndEndDate && benefit.isEnabled) ||
          isEnrolledInHomeImprovementCredit
        )
      })
      .flatMap((benefit) => {
        const cms = cardBenefitsV1[benefit.partnerName]
        if (!cms) {
          sentryCaptureException('Failed to find benefit in Statsig param store', {
            partnerName: benefit.partnerName,
            loadedBenefits: Object.keys(cardBenefitsV1),
          })
          return [] as const
        }

        const validate = CardBenefitSchema.safeParse(cms)

        if (!validate.success) {
          logError(z.treeifyError(validate.error), {
            cms,
            benefit,
            loadedBenefits: Object.keys(cardBenefitsV1),
          })
          return [] as const
        }

        // Special handling for Referral Bonus Offer - only show if eligible (Not enrolled in SEPT50)
        if (benefit.partnerName === REFERRAL_BONUS_OFFER_PARTNER_NAME) {
          const isEligible = referralBonusOfferEligibility

          // Only show if eligible
          if (!isEligible) {
            return [] as const
          }
          return [{ benefit, cms: validate.data }] as const
        }

        if (validate.data.should_hide_from_list) {
          const isEnrolled = userBenefitsEnrollments.find(
            (b) => b.benefitId === benefit.id,
          )?.isEnrolled

          // If Instacart is enabled, show the benefit, even if it's hidden from the list
          if (isInstacartEnabled && benefit.partnerName === INSTACART_PARTNER_NAME) {
            return [{ benefit, cms: validate.data }] as const
          }

          if (!isEnrolled) {
            return [] as const
          }
        }

        return [{ benefit, cms: validate.data }] as const
      })
      .filter(({ benefit }) => {
        if (selectedFilter === 'All') {
          return true
        } else if (selectedFilter === 'Enrolled') {
          return userBenefitsEnrollments.find((b) => b.benefitId === benefit.id)?.isEnrolled
        } else if (selectedFilter === 'Not Enrolled') {
          return !userBenefitsEnrollments.find((b) => b.benefitId === benefit.id)?.isEnrolled
        } else {
          const today = DateTime.now()

          const benefitStartDate = new Date(benefit.startDate)

          const start = DateTime.fromJSDate(benefitStartDate)

          const days = today.diff(start, 'days').toObject().days

          return typeof days === 'number' && days <= 14
        }
      })
      .map((benefitCMS) => {
        const result = Order.safeParse(benefitCMS.benefit.partnerName)
        return {
          ...benefitCMS,
          order: result.success ? order.indexOf(result.data) : Infinity,
        } as const
      })
      .toSorted(({ order: indexA }, { order: indexB }) => indexA - indexB)
  }, [
    cardBenefitsV1,
    benefits,
    userBenefitsEnrollments,
    selectedFilter,
    logError,
    referralBonusOfferEligibility,
    isInstacartLoadingStatus,
    isInstacartEnabledGate,
  ])

  return {
    availableBenefits,
    isLoading:
      isInstacartLoadingStatus !== 'Ready' ||
      isBenefitsLoading ||
      isUserBenefitsEnrollmentsLoading ||
      isReferralBonusOfferEligibilityLoading,
    userBenefitsEnrollments,
  }
}
